import React from "react";

/**
 * Client-side matched-term highlighting for search result rows.
 *
 * <h3>Why this is plain-text only</h3>
 *
 * Every consumer passes a string that the row would otherwise have rendered as a text node — a title,
 * a name, a bio, an excerpt. It must NEVER be pointed at markup. The thought row's body is
 * `dangerouslySetInnerHTML` from `htmlContent`, and string-replacing over markup rewrites the markup:
 * a viewer searching for `div`, `img`, `href` or `class` would have their query matched inside tag
 * names and attribute values, corrupting the HTML and injecting `<mark>` into places no parser
 * expects. Highlighting rich text means walking parsed text nodes, which is a different job and is
 * deliberately out of scope here.
 *
 * <p>The return type is `React.ReactNode`, not a string, for the same reason: nothing this module
 * produces is ever interpolated back into an HTML string.
 *
 * <h3>Why the query does not need threading</h3>
 *
 * Rows already receive `searchAttribution`, whose `query` field is the query as typed. Off the search
 * page — the chooser dialogs reuse these rows — there is no attribution, so `terms` is empty and every
 * consumer falls back to the bare string. That is the correct behaviour rather than a limitation:
 * there is no query to highlight against.
 */

/** Terms shorter than this are skipped: single characters match nearly every row and highlight noise. */
const MIN_TERM_LENGTH = 2;

/**
 * Caps. Both exist because the input is attacker-influenced in the only sense that matters here — a
 * user can paste anything into the search box — and a row is not the place to discover that.
 */
const MAX_TERMS = 8;
const MAX_MARKS_PER_FIELD = 40;

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Splits the raw query into the distinct terms worth highlighting.
 *
 * <p>Sorted LONGEST FIRST, which is load-bearing: the terms are joined into one alternation, and a
 * JavaScript regex alternation is first-match-wins rather than longest-match-wins. With `["cay",
 * "cayos"]` in that order, "cayos" would highlight only its first three letters and leave "os" bare.
 */
export function buildHighlightTerms(query?: string | null): string[] {
    const trimmed = query?.trim();
    if (!trimmed) {
        return [];
    }

    const seen = new Set<string>();
    const terms: string[] = [];

    for (const rawTerm of trimmed.split(/\s+/)) {
        // Quotes are how a viewer asks for a phrase; they are not part of the text being matched.
        const term = rawTerm.replace(/^["'“”‘’]+|["'“”‘’]+$/g, "");
        const key = term.toLowerCase();

        if (term.length < MIN_TERM_LENGTH || seen.has(key)) {
            continue;
        }

        seen.add(key);
        terms.push(term);

        if (terms.length >= MAX_TERMS) {
            break;
        }
    }

    return terms.sort((left, right) => right.length - left.length);
}

/**
 * Renders `text` with every occurrence of every term wrapped in a `<mark>`.
 *
 * <p>Returns the bare string when there is nothing to highlight, so a consumer can call this
 * unconditionally without adding a node to the tree on the no-query path.
 */
export function highlightMatches(text: string | null | undefined, terms: string[]): React.ReactNode {
    if (!text) {
        return text ?? null;
    }
    if (terms.length === 0) {
        return text;
    }

    const pattern = new RegExp(`(${terms.map(escapeRegExp).join("|")})`, "gi");
    // `split` with a capturing group interleaves [between, match, between, match, …], so the odd
    // indices are exactly the matches. This is why the alternation is wrapped in one group and the
    // terms are not: an extra capturing group would shift that parity and mark the wrong segments.
    const segments = text.split(pattern);

    if (segments.length === 1) {
        return text;
    }

    let marksUsed = 0;

    return (
        <>
            {segments.map((segment, index) => {
                if (index % 2 === 1 && marksUsed < MAX_MARKS_PER_FIELD) {
                    marksUsed += 1;
                    return (
                        // Semantic tokens, never the UA default: `mark` is yellow-on-black by default,
                        // which is unreadable on the dark theme's card surface. `text-foreground` is
                        // set explicitly because these sit inside `text-muted-foreground` blocks, and
                        // a highlight that does not also lift the text reads as a smudge.
                        //
                        // The UNDERLINE is the non-colour cue (`DISCOVERY-36`). `bg-primary/20`
                        // measures 1.38:1 against the light card and 1.37:1 against the dark one, and
                        // in a result TITLE the fill is the only cue there is: the `h2` is already
                        // `text-foreground`, so the mark's own text colour changes nothing there. (In
                        // the bio slot it does lift `text-muted-foreground`, which is why only the
                        // title failed.) Raising the alpha is not the fix — the two card surfaces sit
                        // at opposite ends and the primary hue is mid-range, so a value that separates
                        // in one theme flattens in the other. A decoration at `currentColor` survives
                        // a monochrome render, cannot drift with a token, and keeps this module's
                        // contract intact: a plain <mark> around a text node, never markup, because
                        // the thought row's body is `dangerouslySetInnerHTML`.
                        <mark
                            key={`${index}-${segment}`}
                            className="rounded-[3px] bg-primary/20 px-0.5 text-foreground underline decoration-2 underline-offset-2"
                        >
                            {segment}
                        </mark>
                    );
                }
                return <React.Fragment key={`${index}-${segment}`}>{segment}</React.Fragment>;
            })}
        </>
    );
}
